import { useMemo, useState } from 'react';
import { useGetAllAsset, OrderInfoResponse, OrderSide, OrderStatus, OrderType } from '@ping/api';
import { parsePair, t, withMiaxFilter } from '@ping/helpers';
import { StatusIndicator, Text, Tooltip, Popup, Table, Copyable } from '@ping/uikit';
import { compact, format, putSpaceBetweenWords } from '@ping/utils';
import { selectedLanguageSelector, usePreferencesStore } from '@ping/stores/preferences.store';
import { isBusinessLoginSelector, useUserInformationStore } from '@ping/stores/userInformation.store';
import InfoIcon from '@ping/assets/Icon/info.svg';
import { CopyableIcon } from '@ping/uikit/Copyable/CopyableIcon';
import style from './get-orders-columns-list.module.scss';

interface IExtraColumns extends ICustomColumnProperties {
  Header: string | JSX.Element;
  accessor: string;
  Cell?: (any) => JSX.Element;
}

// Component for Token IDs cell with modal
const TokenIdsCell = ({ tokenIds }: { tokenIds: string[] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const transactionIdTableColumns = [
    {
      Header: t('Number'),
      width: '10%',
      Cell: ({ row: { index } }: { row: { index: number } }) => <span>{index + 1}</span>,
    },
    {
      Header: t('Token ID'),
      accessor: 'id',
      Cell: ({ value }: { value: string }) => (
        <Copyable title={t('Copy Token ID')} text={value}>
          <span>{value}</span>
          <CopyableIcon />
        </Copyable>
      ),
    },
  ];

  if (!tokenIds?.length) {
    return <Text> N/A </Text>;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Tooltip
          position='top'
          childrenIcon={
            <Text badge cadetBlueColor>
              {compact.end(tokenIds.join(', '))}
            </Text>
          }
        >
          {tokenIds.join(', ')}
        </Tooltip>
        <InfoIcon style={{ cursor: 'pointer', width: '16px', height: '16px' }} onClick={() => setIsModalOpen(true)} />
      </div>

      <Popup isOpen={isModalOpen} title={t('Token IDs')} onClose={() => setIsModalOpen(false)}>
        <Table
          data={tokenIds.map(id => ({ id }))}
          columns={transactionIdTableColumns}
          isLoading={false}
          initialState={{}}
        />
      </Popup>
    </>
  );
};

const getOrdersColumnsList = (
  extraColumns?: IExtraColumns,
  dependencies?: any[],
  isSortedByUpdatedAt?: boolean,
  isDateFirstColumn?: boolean
) => {
  const selectedLanguage = usePreferencesStore(selectedLanguageSelector);
  const isBusinessLogin = useUserInformationStore(isBusinessLoginSelector);
  const { data: allAssets } = withMiaxFilter(useGetAllAsset());

  const assetScaleMap = new Map(allAssets?.map(item => [item.id, item.scale]));

  return useMemo(
    () => [
      ...(isDateFirstColumn
        ? [
            {
              Header: t('Date & Time'),
              accessor: isSortedByUpdatedAt ? 'updatedAt' : 'createdAt',
              sticky: 'left',
              // More space is needed in this column for the "September" word
              width: 240,
              Cell: ({ value }: { value: string }) => <div>{format.date(value)}</div>,
            },
          ]
        : []),
      {
        Header: t('Pair'),
        accessor: 'instrument',
        sticky: !isDateFirstColumn && 'left',
        width: 200,
        Cell: ({
          value,
          row,
        }: {
          value: string;
          row: { original: OrderInfoResponse & { belongsToCurrentBusinessTrader?: boolean } };
        }) => (
          <span className={style.pair}>
            {parsePair.byInstrument(value).market}
            {isBusinessLogin && row?.original?.belongsToCurrentBusinessTrader === true && (
              <Text badge className={style.yourOrder}>
                {t('Your order')}
              </Text>
            )}
          </span>
        ),
      },
      {
        Header: t('Type of order'),
        accessor: 'orderType',
        width: 110,
        Cell: ({ value }: { value: OrderType }) => <div>{putSpaceBetweenWords(value)}</div>,
      },
      {
        Header: t('Amount'),
        accessor: 'amount',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).base?.toLowerCase()
          );

          return (
            <>
              {format.number(original.amount.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).base}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Side'),
        accessor: 'side',
        width: 50,
        Cell: ({ value }: { value: OrderSide }) => (
          <span className={`status--${value === OrderSide.Buy ? 'green' : 'red'}`}>{value}</span>
        ),
      },
      {
        Header: t('Price'),
        accessor: 'requestedPrice',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).quote?.toLowerCase()
          );

          if (original.requestedPrice == null) {
            return '—';
          }

          return (
            <>
              {format.number(original.requestedPrice.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).quote}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Filled'),
        accessor: 'unitsFilled',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).base?.toLowerCase()
          );

          return (
            <>
              {format.number(original.unitsFilled.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).base}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Not filled'),
        accessor: 'remaining',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).base?.toLowerCase()
          );

          return (
            <>
              {format.number(original.remaining.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).base}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Triggering conditions'),
        accessor: 'executionPrice',
        width: 160,
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).quote?.toLowerCase()
          );

          return (
            <>
              =&gt; &nbsp;
              {format.number(original.stopPrice.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).quote}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Fee'),
        accessor: 'commission',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          return (
            <>
              {format.number(original.commission)}
              &nbsp;
              <Text badge cadetBlueColor>
                {original.feeAsset?.toUpperCase()}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Total'),
        accessor: 'total',
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => {
          const cryptoCurrencyScale = assetScaleMap.get(
            parsePair.byInstrument(original.instrument).quote?.toLowerCase()
          );

          return (
            <>
              {format.number(original.total.toFixed(cryptoCurrencyScale))}
              &nbsp;
              <Text badge cadetBlueColor>
                {parsePair.byInstrument(original.instrument).quote}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Token ids'),
        accessor: 'Token ids',
        width: 140,
        Cell: ({ row: { original } }: { row: { original: OrderInfoResponse } }) => (
          <TokenIdsCell tokenIds={original.tokenIds || []} />
        ),
      },
      {
        Header: t('Status'),
        accessor: 'status',
        width: 115,
        Cell: ({ value, row: { original } }: { value: OrderStatus; row: { original: OrderInfoResponse } }) => (
          <StatusIndicator
            status={value}
            hint={original.cancelReason}
            green={OrderStatus.Completed}
            red={[OrderStatus.Cancelled, OrderStatus.Rejected, OrderStatus.Expired]}
            blue={OrderStatus.Working}
            yellow={[OrderStatus.Pending, OrderStatus.Sending]}
            gray={[OrderStatus.WaitingForPayment]}
          />
        ),
      },
      ...(!isDateFirstColumn
        ? [
            {
              Header: t('Date & Time'),
              accessor: isSortedByUpdatedAt ? 'updatedAt' : 'createdAt',
              Cell: ({ value }: { value: string }) => format.date(value),
            },
          ]
        : []),
      ...(extraColumns ? [extraColumns] : []),
    ],
    [selectedLanguage, isBusinessLogin, allAssets, ...(dependencies || [])]
  );
};

export default getOrdersColumnsList;
